import { useState, useEffect, useCallback, useRef } from "react";
import { BASE_URL } from "../../../config/api";
import toast from "react-hot-toast";

const BRANDS = ["Vivo", "Realme", "Samsung", "OPPO", "Motorola", "Redmi", "Infinix", "Sony", "POCO", "Xiaomi"];

function formatPrice(val) {
  const n = parseFloat(val);
  if (!n || n === 0) return "—";
  return "₹" + n.toLocaleString("en-IN");
}

function formatBytes(bytes) {
  if (!bytes) return "—";
  const b = parseInt(bytes);
  const gb = b / 1073741824;
  return gb >= 1 ? gb.toFixed(1) + " GB" : (b / 1048576).toFixed(0) + " MB";
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function StatCard({ icon, label, value, sub, matchPct, delay }) {
  return (
    <div 
      className="relative overflow-hidden bg-white border border-[#E2E8F0] shadow-sm rounded-[12px] p-[18px_20px] transition-all duration-200 ease-in-out hover:border-[#0ea5a0] hover:-translate-y-0.5 group animate-[fadeUp_0.4s_ease_both]"
      style={{ animationDelay: delay }}
    >
      {/* Top Hover Gradient Indicator */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#0A4ABF] to-[#0ea5a0] opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
      
      <div className="w-9 h-9 rounded-lg flex items-center justify-center text-base mb-3 bg-gradient-to-br from-[#0A4ABF] to-[#0ea5a0] text-white shadow-sm">
        <span>{icon}</span>
      </div>
      
      <div className="text-[26px] font-bold font-mono tracking-tight leading-none mb-1 text-[#0F172A]">
        {value}
      </div>
      
      <div className="text-xs text-[#64748B] font-semibold uppercase tracking-[0.5px]">
        {label}
      </div>
      
      {sub && <div className="text-[11px] text-[#94A3B8] mt-1.5 font-medium">{sub}</div>}
      
      {matchPct !== undefined && (
        <div className="h-[4px] bg-[#F1F5F9] rounded-[2px] mt-2.5 overflow-hidden">
          <div 
            className="h-full rounded-[2px] bg-gradient-to-r from-[#0ea5a0] to-[#1ED6C3] transition-[width] duration-1000 ease-out" 
            style={{ width: `${matchPct}%` }} 
          />
        </div>
      )}
    </div>
  );
}

function RankChart({ title, icon, items, maxVal }) {
  return (
    <div className="bg-white border border-[#E2E8F0] shadow-sm rounded-[12px] overflow-hidden animate-[fadeUp_0.5s_ease_both]">
      <div className="p-[16px_20px] border-b border-[#F1F5F9] flex items-center justify-between">
        <div className="text-xs font-bold text-[#0F172A] flex items-center gap-2">
          <span className="text-sm">{icon}</span>
          {title}
        </div>
        <span className="text-[11px] font-semibold text-[#94A3B8]">Top {items.length}</span>
      </div>
      <div className="py-2">
        {items.map((item, i) => (
          <div className="flex items-center gap-3 p-[10px_20px] transition-colors duration-150 hover:bg-[#F8FAFC]" key={i}>
            <div className="w-5 text-[11px] font-bold text-[#94A3B8] text-center flex-shrink-0 font-mono">
              {String(i + 1).padStart(2, "0")}
            </div>
            <div className="flex-1">
              <div className="text-xs font-semibold text-[#334155] mb-1">
                {item.full_model_name || item.brand || "—"}
              </div>
              <div className="h-1 bg-[#F1F5F9] rounded-[2px] overflow-hidden">
                <div 
                  className="h-full rounded-[2px] bg-gradient-to-r from-[#0A4ABF] to-[#0ea5a0] transition-[width] duration-800 ease-out" 
                  style={{ width: `${(item.total / maxVal) * 100}%` }} 
                />
              </div>
            </div>
            <div className="text-xs font-bold font-mono text-[#64748B] flex-shrink-0 min-w-9 text-right">
              {item.total.toLocaleString()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SkeletonRows({ n = 8 }) {
  return Array.from({ length: n }).map((_, i) => (
    <tr className="animate-pulse" key={i}>
      {Array.from({ length: 9 }).map((_, j) => (
        <td className="p-3.5 border-b border-[#F1F5F9]" key={j}>
          <div 
            className="h-3.5 bg-gradient-to-r from-[#F1F5F9] via-[#E2E8F0] to-[#F1F5F9] bg-[length:200%_100%] rounded animate-[shimmer_1.5s_infinite]" 
            style={{ width: j === 0 ? "130px" : j === 1 ? "80px" : "100px" }} 
          />
        </td>
      ))}
    </tr>
  ));
}

function Paginator({ page, lastPage, onPage }) {
  const pages = [];
  const delta = 2;
  const left = Math.max(1, page - delta);
  const right = Math.min(lastPage, page + delta);
  if (left > 1) pages.push(1, "...");
  for (let p = left; p <= right; p++) pages.push(p);
  if (right < lastPage) pages.push("...", lastPage);

  return (
    <div className="flex items-center gap-1.5">
      <button 
        className="w-8 h-8 bg-white border border-[#CBD5E1] rounded-lg text-xs font-medium text-[#64748B] flex items-center justify-center transition-all duration-150 enabled:hover:border-[#0ea5a0] enabled:hover:text-[#0F172A] disabled:opacity-40 disabled:cursor-not-allowed font-sans" 
        onClick={() => onPage(page - 1)} 
        disabled={page === 1}
      >
        ‹
      </button>
      {pages.map((p, i) =>
        p === "..." ? (
          <span key={i} className="text-[#94A3B8] text-xs px-1">…</span>
        ) : (
          <button
            key={i}
            className={`w-8 h-8 border rounded-lg text-xs font-medium flex items-center justify-center transition-all duration-150 font-sans ${
              p === page 
                ? "bg-gradient-to-br from-[#0A4ABF] to-[#0ea5a0] border-transparent text-white font-bold shadow-sm" 
                : "bg-white border-[#CBD5E1] text-[#64748B] enabled:hover:border-[#0ea5a0] enabled:hover:text-[#0F172A]"
            }`}
            onClick={() => onPage(p)}
          >
            {p}
          </button>
        )
      )}
      <button 
        className="w-8 h-8 bg-white border border-[#CBD5E1] rounded-lg text-xs font-medium text-[#64748B] flex items-center justify-center transition-all duration-150 enabled:hover:border-[#0ea5a0] enabled:hover:text-[#0F172A] disabled:opacity-40 disabled:cursor-not-allowed font-sans" 
        onClick={() => onPage(page + 1)} 
        disabled={page === lastPage}
      >
        ›
      </button>
    </div>
  );
}

export default function SyncedDevicesPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    search: "", match_status: "", brand: "", from_date: "", to_date: "", per_page: 20,
  });

  const [applied, setApplied] = useState({ ...filters });
  const [page, setPage] = useState(1);

  const debounceRef = useRef(null);

  const fetchData = useCallback(async (f, pg) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (f.search) params.set("search", f.search);
      if (f.match_status) params.set("match_status", f.match_status);
      if (f.brand) params.set("brand", f.brand);
      if (f.from_date) params.set("from_date", f.from_date);
      if (f.to_date) params.set("to_date", f.to_date);
      params.set("per_page", f.per_page);
      params.set("page", pg);

      const res = await fetch(`${BASE_URL}synced-devices?${params}`);
      if (!res.ok) throw new Error(`${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(applied, page);
  }, [applied, page, fetchData]);

  const handleSearchChange = (val) => {
    setFilters(f => ({ ...f, search: val }));
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setApplied(f => ({ ...f, search: val }));
      setPage(1);
    }, 450);
  };

  const applyFilters = () => { setApplied({ ...filters }); setPage(1); };

  const resetFilters = () => {
    const blank = { search: "", match_status: "", brand: "", from_date: "", to_date: "", per_page: 20 };
    setFilters(blank);
    setApplied(blank);
    setPage(1);
  };

  const setFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    
    // Create a loading state promise toast
    const syncToastId = toast.loading("Initiating data synchronization...");
    
    try {
      const res = await fetch(`${BASE_URL}synced-devices/re-match`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      if (!res.ok) throw new Error("Sync workflow execution failed");
      
      const json = await res.json();
      
      if (json.success) {
        toast.success(json.message || "Re-match started in background", { id: syncToastId });
        // Optionally reload modern client data status after initiating sync
        fetchData(applied, page);
      } else {
        toast.error(json.message || "Unable to process synchronization request.", { id: syncToastId });
      }
    } catch (err) {
      toast.error(err.message || "Network unexpected interruption error.", { id: syncToastId });
    } finally {
      setSyncing(false);
    }
  };

  const summary = data?.summary || {};
  const topModels = data?.top_models || [];
  const topBrands = data?.top_brands || [];
  const records = data?.data || [];
  const pagination = data?.pagination || {};

  const maxModel = topModels[0]?.total || 1;
  const maxBrand = topBrands[0]?.total || 1;

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-sans text-[#334155]">
      {/* Topbar Navigation */}
      <nav className="sticky top-0 z-[100] bg-white/90 backdrop-blur-[20px] border-b border-[#E2E8F0] shadow-sm px-6 h-[60px] flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="bg-gradient-to-br from-[#0A4ABF] to-[#0ea5a0] rounded-lg w-8 h-8 flex items-center justify-center text-sm font-bold text-white shadow-sm">
            G
          </div>
          <div>
            <div className="text-sm font-bold text-[#0F172A] tracking-tight">GoElectronix</div>
            <div className="text-[11px] text-[#64748B] font-medium">Synced Devices Intelligence</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 bg-[#E6F4EA] border border-[#A3E635]/30 rounded-[20px] p-[4px_10px] text-[11px] font-bold text-[#137333]">
            <div className="w-1.5 h-1.5 rounded-full bg-[#137333] animate-[pulse_2s_infinite]" />
            Live Data
          </div>
          <button 
            className="h-9 px-4 border rounded-lg bg-white text-[#64748B] border-[#CBD5E1] hover:border-[#0ea5a0] hover:text-[#0F172A] font-sans text-xs font-semibold cursor-pointer transition-all duration-200 shadow-sm flex items-center gap-1.5" 
            onClick={() => fetchData(applied, page)}
            disabled={loading}
          >
            {loading ? <div className="w-4 h-4 border-2 border-[rgba(14,165,160,0.3)] border-t-[#0ea5a0] rounded-full animate-spin flex-shrink-0" /> : "↻"} Refresh
          </button>
          <button 
            className="h-9 px-4 border rounded-lg bg-white text-[#64748B] border-[#CBD5E1] enabled:hover:border-[#0ea5a0] enabled:hover:text-[#0F172A] font-sans text-xs font-semibold cursor-pointer transition-all duration-200 shadow-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed" 
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? <div className="w-4 h-4 border-2 border-[rgba(14,165,160,0.3)] border-t-[#0ea5a0] rounded-full animate-spin flex-shrink-0" /> : "⚡"} Sync
          </button>
        </div>
      </nav>

      {/* Main Workspace Layout Container */}
      <div className="p-6 max-w-[1600px] mx-auto">
        {error && (
          <div className="bg-[#FEF2F2] border border-[#FCA5A5] rounded-lg p-[12px_16px] text-xs text-[#991B1B] font-medium mb-4 flex items-center gap-2 shadow-sm">
            <span>⚠️</span> {error} — check your network or API status.
          </div>
        )}

        {/* Global Overview Dashboard Metrics Section */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3.5 mb-6">
          <StatCard icon="📱" label="Total Devices" value={summary.total_records?.toLocaleString() ?? "—"} delay="0ms" />
          <StatCard icon="✅" label="Matched" value={summary.matched?.toLocaleString() ?? "—"} sub={`${summary.match_percentage ?? 0}% match rate`} matchPct={summary.match_percentage} delay="60ms" />
          <StatCard icon="⏳" label="Unmatched" value={summary.not_matched?.toLocaleString() ?? "—"} delay="120ms" />
          <StatCard icon="🤖" label="AI Processed" value={summary.ai_processed?.toLocaleString() ?? "—"} sub={summary.total_records ? Math.round((summary.ai_processed / summary.total_records) * 100) + "% enriched" : ""} delay="180ms" />
          <StatCard icon="📊" label="Match Rate" value={(summary.match_percentage ?? 0) + "%"} matchPct={summary.match_percentage} delay="240ms" />
        </div>

        {/* High Volume Metrics Analytics Charts Grid */}
        {topModels.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 mb-6">
            <RankChart title="Top Models" icon="📱" items={topModels} maxVal={maxModel} />
            <RankChart title="Top Brands" icon="🏷️" items={topBrands.filter(b => b.brand)} maxVal={maxBrand} />
          </div>
        )}

        {/* Search Context and Dynamic Parameters Panel */}
        <div className="bg-white border border-[#E2E8F0] shadow-sm rounded-[12px] p-[16px_20px] mb-5 animate-[fadeUp_0.55s_ease_both]">
          <div className="flex flex-wrap gap-2.5 items-end">
            <div className="flex flex-col gap-1.5 min-w-[220px] flex-1">
              <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px]">Search</label>
              <input 
                className="bg-[#F8FAFC] border border-[#CBD5E1] rounded-lg text-[#0F172A] font-sans text-xs p-[8px_12px] outline-none transition-colors duration-200 h-9 placeholder-[#94A3B8] focus:border-[#0ea5a0] focus:bg-white" 
                placeholder="Name, IMEI, model, mobile…"
                value={filters.search} 
                onChange={e => handleSearchChange(e.target.value)} 
              />
            </div>

            <div className="flex flex-col gap-1.5 min-w-[150px]">
              <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px]">Match Status</label>
              <select 
                className="bg-[#F8FAFC] border border-[#CBD5E1] rounded-lg text-[#0F172A] font-sans text-xs p-[8px_12px] outline-none transition-colors duration-200 h-9 focus:border-[#0ea5a0] focus:bg-white" 
                value={filters.match_status} 
                onChange={e => setFilter("match_status", e.target.value)}
              >
                <option value="" className="bg-white">All</option>
                <option value="matched" className="bg-white">Matched</option>
                <option value="not_matched" className="bg-white">Not Matched</option>
              </select>
            </div>

            <div className="flex flex-col gap-1.5 min-w-[150px]">
              <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px]">Brand</label>
              <select 
                className="bg-[#F8FAFC] border border-[#CBD5E1] rounded-lg text-[#0F172A] font-sans text-xs p-[8px_12px] outline-none transition-colors duration-200 h-9 focus:border-[#0ea5a0] focus:bg-white" 
                value={filters.brand} 
                onChange={e => setFilter("brand", e.target.value)}
              >
                <option value="" className="bg-white">All Brands</option>
                {BRANDS.map(b => <option key={b} value={b} className="bg-white">{b}</option>)}
              </select>
            </div>

            <div className="flex flex-col gap-1.5 min-w-[150px]">
              <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px]">From Date</label>
              <input 
                type="date" 
                className="bg-[#F8FAFC] border border-[#CBD5E1] rounded-lg text-[#0F172A] font-sans text-xs p-[8px_12px] outline-none transition-colors duration-200 h-9 focus:border-[#0ea5a0] focus:bg-white" 
                value={filters.from_date} 
                onChange={e => setFilter("from_date", e.target.value)} 
              />
            </div>

            <div className="flex flex-col gap-1.5 min-w-[150px]">
              <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px]">To Date</label>
              <input 
                type="date" 
                className="bg-[#F8FAFC] border border-[#CBD5E1] rounded-lg text-[#0F172A] font-sans text-xs p-[8px_12px] outline-none transition-colors duration-200 h-9 focus:border-[#0ea5a0] focus:bg-white" 
                value={filters.to_date} 
                onChange={e => setFilter("to_date", e.target.value)} 
              />
            </div>

            <div className="flex flex-col gap-1.5 min-w-[150px]">
              <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px]">Per Page</label>
              <select 
                className="bg-[#F8FAFC] border border-[#CBD5E1] rounded-lg text-[#0F172A] font-sans text-xs p-[8px_12px] outline-none transition-colors duration-200 h-9 focus:border-[#0ea5a0] focus:bg-white" 
                value={filters.per_page} 
                onChange={e => setFilter("per_page", parseInt(e.target.value))}
              >
                {[10, 20, 50, 100].map(n => <option key={n} value={n} className="bg-white">{n}</option>)}
              </select>
            </div>

            <div className="flex gap-2 items-end">
              <button 
                className="h-9 px-4 border border-transparent rounded-lg bg-gradient-to-br from-[#0A4ABF] to-[#0ea5a0] text-white font-sans text-xs font-semibold cursor-pointer transition-all duration-200 shadow-sm flex items-center gap-1.5 hover:opacity-90 hover:-translate-y-0.5" 
                onClick={applyFilters}
              >
                Apply
              </button>
              <button 
                className="h-9 px-4 border rounded-lg bg-white text-[#64748B] border-[#CBD5E1] hover:border-[#0ea5a0] hover:text-[#0F172A] font-sans text-xs font-semibold cursor-pointer transition-all duration-200 shadow-sm flex items-center gap-1.5" 
                onClick={resetFilters}
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Tabular Relational Database Display Workspace */}
        <div className="bg-white border border-[#E2E8F0] shadow-sm rounded-[12px] overflow-hidden animate-[fadeUp_0.6s_ease_both]">
          <div className="p-[14px_20px] border-b border-[#F1F5F9] flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs font-bold text-[#0F172A]">Device Records</div>
            <div className="flex gap-3 items-center">
              {loading && <div className="w-4 h-4 border-2 border-[rgba(14,165,160,0.3)] border-t-[#0ea5a0] rounded-full animate-spin flex-shrink-0" />}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-[#F8FAFC]">
                  <th className="p-[10px_14px] text-left text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px] border-b border-[#E2E8F0] whitespace-nowrap">Customer</th>
                  <th className="p-[10px_14px] text-left text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px] border-b border-[#E2E8F0] whitespace-nowrap">Mobile</th>
                  <th className="p-[10px_14px] text-left text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px] border-b border-[#E2E8F0] whitespace-nowrap">IMEI 1</th>
                  <th className="p-[10px_14px] text-left text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px] border-b border-[#E2E8F0] whitespace-nowrap">Model</th>
                  <th className="p-[10px_14px] text-left text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px] border-b border-[#E2E8F0] whitespace-nowrap">AI Model</th>
                  <th className="p-[10px_14px] text-left text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px] border-b border-[#E2E8F0] whitespace-nowrap">Variant</th>
                  <th className="p-[10px_14px] text-left text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px] border-b border-[#E2E8F0] whitespace-nowrap">Price</th>
                  <th className="p-[10px_14px] text-left text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px] border-b border-[#E2E8F0] whitespace-nowrap">Match</th>
                  <th className="p-[10px_14px] text-left text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px] border-b border-[#E2E8F0] whitespace-nowrap">Synced</th>
                </tr>
              </thead>
              <tbody>
                {loading && records.length === 0 ? (
                  <SkeletonRows n={applied.per_page > 20 ? 10 : applied.per_page} />
                ) : records.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-3.5 border-b-0">
                      <div className="text-center p-[60px_20px] text-[#94A3B8]">
                        <div className="text-4xl mb-3">🔍</div>
                        <div className="text-[15px] font-bold text-[#64748B] mb-1.5">No records found</div>
                        <div className="text-xs font-medium">Try adjusting your search or filters</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  records.map(r => (
                    <tr key={r.id} className="group hover:bg-[#F8FAFC] transition-colors duration-100">
                      <td className="p-[12px_14px] border-b border-[#F1F5F9] group-last:border-b-0 vertical-middle text-[#475569]">
                        <div className="text-[#0F172A] font-semibold">{r.customer_name || "—"}</div>
                        <div className="text-[11px] text-[#64748B] mt-0.5 font-medium">{r.email || ""}</div>
                      </td>
                      <td className="p-[12px_14px] border-b border-[#F1F5F9] group-last:border-b-0 vertical-middle text-[#334155] font-mono text-xs">
                        {r.mobile || "—"}
                      </td>
                      <td className="p-[12px_14px] border-b border-[#F1F5F9] group-last:border-b-0 vertical-middle font-mono text-xs text-[#64748B]">
                        {r.imei1 || "—"}
                      </td>
                      <td className="p-[12px_14px] border-b border-[#F1F5F9] group-last:border-b-0 vertical-middle text-[#475569]">
                        <div className="text-xs font-semibold text-[#334155]">{r.brand || "—"}</div>
                        <div className="text-xs text-[#64748B] font-mono">{r.model || ""}</div>
                      </td>
                      <td className="p-[12px_14px] border-b border-[#F1F5F9] group-last:border-b-0 vertical-middle text-[#097975] font-bold">
                        {r.full_model_name || <span className="text-[#94A3B8]">—</span>}
                      </td>
                      <td className="p-[12px_14px] border-b border-[#F1F5F9] group-last:border-b-0 vertical-middle text-xs text-[#475569] font-medium">
                        {r.variant || "—"}
                      </td>
                      <td className="p-[12px_14px] border-b border-[#F1F5F9] group-last:border-b-0 vertical-middle font-mono text-xs text-[#0ea5a0] font-bold">
                        {formatPrice(r.approx_price || r.device_price)}
                      </td>
                      <td className="p-[12px_14px] border-b border-[#F1F5F9] group-last:border-b-0 vertical-middle text-[#475569]">
                        {r.is_matched ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[20px] text-[11px] font-bold bg-[#E6F4EA] border border-[#A3E635]/20 text-[#137333]">
                            ✓ Matched
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[20px] text-[11px] font-bold bg-[#FEF3C7] border border-[#F59E0B]/20 text-[#B45309]">
                            Pending
                          </span>
                        )
                        }
                        {r.ai_processed && (
                          <div className="mt-1">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[20px] text-[11px] font-bold bg-[#E0F2FE] border border-[#0EA5E9]/20 text-[#0369A1]">
                              AI ✦
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="p-[12px_14px] border-b border-[#F1F5F9] group-last:border-b-0 vertical-middle text-[11px] text-[#64748B] font-medium whitespace-nowrap">
                        {formatDate(r.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Navigation Ecosystem footer section */}
          {pagination.last_page > 1 && (
            <div className="p-[16px_20px] border-t border-[#F1F5F9] flex items-center justify-between flex-wrap gap-2.5 bg-[#F8FAFC]">
              <div className="text-xs text-[#64748B] font-medium">
                Page {pagination.current_page} of {pagination.last_page} · {pagination.total?.toLocaleString()} total
              </div>
              <Paginator
                page={pagination.current_page} 
                lastPage={pagination.last_page} 
                onPage={p => { 
                  setPage(p); 
                  window.scrollTo({ top: 0, behavior: "smooth" }); 
                }} 
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// import { useState, useEffect, useCallback, useRef } from "react";
// import { BASE_URL } from "../../../config/api";

// const BRANDS = ["Vivo", "Realme", "Samsung", "OPPO", "Motorola", "Redmi", "Infinix", "Sony", "POCO", "Xiaomi"];

// function formatPrice(val) {
//   const n = parseFloat(val);
//   if (!n || n === 0) return "—";
//   return "₹" + n.toLocaleString("en-IN");
// }

// function formatBytes(bytes) {
//   if (!bytes) return "—";
//   const b = parseInt(bytes);
//   const gb = b / 1073741824;
//   return gb >= 1 ? gb.toFixed(1) + " GB" : (b / 1048576).toFixed(0) + " MB";
// }

// function formatDate(iso) {
//   if (!iso) return "—";
//   return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
// }

// function StatCard({ icon, label, value, sub, matchPct, delay }) {
//   return (
//     <div 
//       className="relative overflow-hidden bg-white border border-[#E2E8F0] shadow-sm rounded-[12px] p-[18px_20px] transition-all duration-200 ease-in-out hover:border-[#0ea5a0] hover:-translate-y-0.5 group animate-[fadeUp_0.4s_ease_both]"
//       style={{ animationDelay: delay }}
//     >
//       {/* Top Hover Gradient Indicator */}
//       <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#0A4ABF] to-[#0ea5a0] opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
      
//       <div className="w-9 h-9 rounded-lg flex items-center justify-center text-base mb-3 bg-gradient-to-br from-[#0A4ABF] to-[#0ea5a0] text-white shadow-sm">
//         <span>{icon}</span>
//       </div>
      
//       <div className="text-[26px] font-bold font-mono tracking-tight leading-none mb-1 text-[#0F172A]">
//         {value}
//       </div>
      
//       <div className="text-xs text-[#64748B] font-semibold uppercase tracking-[0.5px]">
//         {label}
//       </div>
      
//       {sub && <div className="text-[11px] text-[#94A3B8] mt-1.5 font-medium">{sub}</div>}
      
//       {matchPct !== undefined && (
//         <div className="h-[4px] bg-[#F1F5F9] rounded-[2px] mt-2.5 overflow-hidden">
//           <div 
//             className="h-full rounded-[2px] bg-gradient-to-r from-[#0ea5a0] to-[#1ED6C3] transition-[width] duration-1000 ease-out" 
//             style={{ width: `${matchPct}%` }} 
//           />
//         </div>
//       )}
//     </div>
//   );
// }

// function RankChart({ title, icon, items, maxVal }) {
//   return (
//     <div className="bg-white border border-[#E2E8F0] shadow-sm rounded-[12px] overflow-hidden animate-[fadeUp_0.5s_ease_both]">
//       <div className="p-[16px_20px] border-b border-[#F1F5F9] flex items-center justify-between">
//         <div className="text-xs font-bold text-[#0F172A] flex items-center gap-2">
//           <span className="text-sm">{icon}</span>
//           {title}
//         </div>
//         <span className="text-[11px] font-semibold text-[#94A3B8]">Top {items.length}</span>
//       </div>
//       <div className="py-2">
//         {items.map((item, i) => (
//           <div className="flex items-center gap-3 p-[10px_20px] transition-colors duration-150 hover:bg-[#F8FAFC]" key={i}>
//             <div className="w-5 text-[11px] font-bold text-[#94A3B8] text-center flex-shrink-0 font-mono">
//               {String(i + 1).padStart(2, "0")}
//             </div>
//             <div className="flex-1">
//               <div className="text-xs font-semibold text-[#334155] mb-1">
//                 {item.full_model_name || item.brand || "—"}
//               </div>
//               <div className="h-1 bg-[#F1F5F9] rounded-[2px] overflow-hidden">
//                 <div 
//                   className="h-full rounded-[2px] bg-gradient-to-r from-[#0A4ABF] to-[#0ea5a0] transition-[width] duration-800 ease-out" 
//                   style={{ width: `${(item.total / maxVal) * 100}%` }} 
//                 />
//               </div>
//             </div>
//             <div className="text-xs font-bold font-mono text-[#64748B] flex-shrink-0 min-w-9 text-right">
//               {item.total.toLocaleString()}
//             </div>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// }

// function SkeletonRows({ n = 8 }) {
//   return Array.from({ length: n }).map((_, i) => (
//     <tr className="animate-pulse" key={i}>
//       {Array.from({ length: 9 }).map((_, j) => (
//         <td className="p-3.5 border-b border-[#F1F5F9]" key={j}>
//           <div 
//             className="h-3.5 bg-gradient-to-r from-[#F1F5F9] via-[#E2E8F0] to-[#F1F5F9] bg-[length:200%_100%] rounded animate-[shimmer_1.5s_infinite]" 
//             style={{ width: j === 0 ? "130px" : j === 1 ? "80px" : "100px" }} 
//           />
//         </td>
//       ))}
//     </tr>
//   ));
// }

// function Paginator({ page, lastPage, onPage }) {
//   const pages = [];
//   const delta = 2;
//   const left = Math.max(1, page - delta);
//   const right = Math.min(lastPage, page + delta);
//   if (left > 1) pages.push(1, "...");
//   for (let p = left; p <= right; p++) pages.push(p);
//   if (right < lastPage) pages.push("...", lastPage);

//   return (
//     <div className="flex items-center gap-1.5">
//       <button 
//         className="w-8 h-8 bg-white border border-[#CBD5E1] rounded-lg text-xs font-medium text-[#64748B] flex items-center justify-center transition-all duration-150 enabled:hover:border-[#0ea5a0] enabled:hover:text-[#0F172A] disabled:opacity-40 disabled:cursor-not-allowed font-sans" 
//         onClick={() => onPage(page - 1)} 
//         disabled={page === 1}
//       >
//         ‹
//       </button>
//       {pages.map((p, i) =>
//         p === "..." ? (
//           <span key={i} className="text-[#94A3B8] text-xs px-1">…</span>
//         ) : (
//           <button
//             key={i}
//             className={`w-8 h-8 border rounded-lg text-xs font-medium flex items-center justify-center transition-all duration-150 font-sans ${
//               p === page 
//                 ? "bg-gradient-to-br from-[#0A4ABF] to-[#0ea5a0] border-transparent text-white font-bold shadow-sm" 
//                 : "bg-white border-[#CBD5E1] text-[#64748B] enabled:hover:border-[#0ea5a0] enabled:hover:text-[#0F172A]"
//             }`}
//             onClick={() => onPage(p)}
//           >
//             {p}
//           </button>
//         )
//       )}
//       <button 
//         className="w-8 h-8 bg-white border border-[#CBD5E1] rounded-lg text-xs font-medium text-[#64748B] flex items-center justify-center transition-all duration-150 enabled:hover:border-[#0ea5a0] enabled:hover:text-[#0F172A] disabled:opacity-40 disabled:cursor-not-allowed font-sans" 
//         onClick={() => onPage(page + 1)} 
//         disabled={page === lastPage}
//       >
//         ›
//       </button>
//     </div>
//   );
// }

// export default function SyncedDevicesPanel() {
//   const [data, setData] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   const [filters, setFilters] = useState({
//     search: "", match_status: "", brand: "", from_date: "", to_date: "", per_page: 20,
//   });

//   const [applied, setApplied] = useState({ ...filters });
//   const [page, setPage] = useState(1);

//   const debounceRef = useRef(null);

//   const fetchData = useCallback(async (f, pg) => {
//     setLoading(true);
//     setError(null);
//     try {
//       const params = new URLSearchParams();
//       if (f.search) params.set("search", f.search);
//       if (f.match_status) params.set("match_status", f.match_status);
//       if (f.brand) params.set("brand", f.brand);
//       if (f.from_date) params.set("from_date", f.from_date);
//       if (f.to_date) params.set("to_date", f.to_date);
//       params.set("per_page", f.per_page);
//       params.set("page", pg);

//       const res = await fetch(`${BASE_URL}synced-devices?${params}`);
//       if (!res.ok) throw new Error(`${res.status}`);
//       const json = await res.json();
//       setData(json);
//     } catch (e) {
//       setError(e.message);
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   useEffect(() => {
//     fetchData(applied, page);
//   }, [applied, page, fetchData]);

//   const handleSearchChange = (val) => {
//     setFilters(f => ({ ...f, search: val }));
//     clearTimeout(debounceRef.current);
//     debounceRef.current = setTimeout(() => {
//       setApplied(f => ({ ...f, search: val }));
//       setPage(1);
//     }, 450);
//   };

//   const applyFilters = () => { setApplied({ ...filters }); setPage(1); };

//   const resetFilters = () => {
//     const blank = { search: "", match_status: "", brand: "", from_date: "", to_date: "", per_page: 20 };
//     setFilters(blank);
//     setApplied(blank);
//     setPage(1);
//   };

//   const setFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));

//   const summary = data?.summary || {};
//   const topModels = data?.top_models || [];
//   const topBrands = data?.top_brands || [];
//   const records = data?.data || [];
//   const pagination = data?.pagination || {};

//   const maxModel = topModels[0]?.total || 1;
//   const maxBrand = topBrands[0]?.total || 1;

//   return (
//     <div className="min-h-screen bg-[#F8FAFC] font-sans text-[#334155]">
//       {/* Topbar Navigation */}
//       <nav className="sticky top-0 z-[100] bg-white/90 backdrop-blur-[20px] border-b border-[#E2E8F0] shadow-sm px-6 h-[60px] flex items-center justify-between">
//         <div className="flex items-center gap-2.5">
//           <div className="bg-gradient-to-br from-[#0A4ABF] to-[#0ea5a0] rounded-lg w-8 h-8 flex items-center justify-center text-sm font-bold text-white shadow-sm">
//             G
//           </div>
//           <div>
//             <div className="text-sm font-bold text-[#0F172A] tracking-tight">GoElectronix</div>
//             <div className="text-[11px] text-[#64748B] font-medium">Synced Devices Intelligence</div>
//           </div>
//         </div>
//         <div className="flex items-center gap-3">
//           <div className="flex items-center gap-1.5 bg-[#E6F4EA] border border-[#A3E635]/30 rounded-[20px] p-[4px_10px] text-[11px] font-bold text-[#137333]">
//             <div className="w-1.5 h-1.5 rounded-full bg-[#137333] animate-[pulse_2s_infinite]" />
//             Live Data
//           </div>
//           <button 
//             className="h-9 px-4 border rounded-lg bg-white text-[#64748B] border-[#CBD5E1] hover:border-[#0ea5a0] hover:text-[#0F172A] font-sans text-xs font-semibold cursor-pointer transition-all duration-200 shadow-sm flex items-center gap-1.5" 
//             onClick={() => fetchData(applied, page)}
//           >
//             {loading ? <div className="w-4 h-4 border-2 border-[rgba(14,165,160,0.3)] border-t-[#0ea5a0] rounded-full animate-spin flex-shrink-0" /> : "↻"} Refresh
//           </button>
//           <button 
//             className="h-9 px-4 border rounded-lg bg-white text-[#64748B] border-[#CBD5E1] hover:border-[#0ea5a0] hover:text-[#0F172A] font-sans text-xs font-semibold cursor-pointer transition-all duration-200 shadow-sm flex items-center gap-1.5" 
//             onClick={() => fetchData(applied, page)}
//           >
//             {loading ? <div className="w-4 h-4 border-2 border-[rgba(14,165,160,0.3)] border-t-[#0ea5a0] rounded-full animate-spin flex-shrink-0" /> : "↻"} Sync
//           </button>
//         </div>
//       </nav>

//       {/* Main Workspace Layout Container */}
//       <div className="p-6 max-w-[1600px] mx-auto">
//         {error && (
//           <div className="bg-[#FEF2F2] border border-[#FCA5A5] rounded-lg p-[12px_16px] text-xs text-[#991B1B] font-medium mb-4 flex items-center gap-2 shadow-sm">
//             <span>⚠️</span> {error} — check your network or API status.
//           </div>
//         )}

//         {/* Global Overview Dashboard Metrics Section */}
//         <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3.5 mb-6">
//           <StatCard icon="📱" label="Total Devices" value={summary.total_records?.toLocaleString() ?? "—"} delay="0ms" />
//           <StatCard icon="✅" label="Matched" value={summary.matched?.toLocaleString() ?? "—"} sub={`${summary.match_percentage ?? 0}% match rate`} matchPct={summary.match_percentage} delay="60ms" />
//           <StatCard icon="⏳" label="Unmatched" value={summary.not_matched?.toLocaleString() ?? "—"} delay="120ms" />
//           <StatCard icon="🤖" label="AI Processed" value={summary.ai_processed?.toLocaleString() ?? "—"} sub={summary.total_records ? Math.round((summary.ai_processed / summary.total_records) * 100) + "% enriched" : ""} delay="180ms" />
//           <StatCard icon="📊" label="Match Rate" value={(summary.match_percentage ?? 0) + "%"} matchPct={summary.match_percentage} delay="240ms" />
//         </div>

//         {/* High Volume Metrics Analytics Charts Grid */}
//         {topModels.length > 0 && (
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 mb-6">
//             <RankChart title="Top Models" icon="📱" items={topModels} maxVal={maxModel} />
//             <RankChart title="Top Brands" icon="🏷️" items={topBrands.filter(b => b.brand)} maxVal={maxBrand} />
//           </div>
//         )}

//         {/* Search Context and Dynamic Parameters Panel */}
//         <div className="bg-white border border-[#E2E8F0] shadow-sm rounded-[12px] p-[16px_20px] mb-5 animate-[fadeUp_0.55s_ease_both]">
//           <div className="flex flex-wrap gap-2.5 items-end">
//             <div className="flex flex-col gap-1.5 min-w-[220px] flex-1">
//               <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px]">Search</label>
//               <input 
//                 className="bg-[#F8FAFC] border border-[#CBD5E1] rounded-lg text-[#0F172A] font-sans text-xs p-[8px_12px] outline-none transition-colors duration-200 h-9 placeholder-[#94A3B8] focus:border-[#0ea5a0] focus:bg-white" 
//                 placeholder="Name, IMEI, model, mobile…"
//                 value={filters.search} 
//                 onChange={e => handleSearchChange(e.target.value)} 
//               />
//             </div>

//             <div className="flex flex-col gap-1.5 min-w-[150px]">
//               <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px]">Match Status</label>
//               <select 
//                 className="bg-[#F8FAFC] border border-[#CBD5E1] rounded-lg text-[#0F172A] font-sans text-xs p-[8px_12px] outline-none transition-colors duration-200 h-9 focus:border-[#0ea5a0] focus:bg-white" 
//                 value={filters.match_status} 
//                 onChange={e => setFilter("match_status", e.target.value)}
//               >
//                 <option value="" className="bg-white">All</option>
//                 <option value="matched" className="bg-white">Matched</option>
//                 <option value="not_matched" className="bg-white">Not Matched</option>
//               </select>
//             </div>

//             <div className="flex flex-col gap-1.5 min-w-[150px]">
//               <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px]">Brand</label>
//               <select 
//                 className="bg-[#F8FAFC] border border-[#CBD5E1] rounded-lg text-[#0F172A] font-sans text-xs p-[8px_12px] outline-none transition-colors duration-200 h-9 focus:border-[#0ea5a0] focus:bg-white" 
//                 value={filters.brand} 
//                 onChange={e => setFilter("brand", e.target.value)}
//               >
//                 <option value="" className="bg-white">All Brands</option>
//                 {BRANDS.map(b => <option key={b} value={b} className="bg-white">{b}</option>)}
//               </select>
//             </div>

//             <div className="flex flex-col gap-1.5 min-w-[150px]">
//               <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px]">From Date</label>
//               <input 
//                 type="date" 
//                 className="bg-[#F8FAFC] border border-[#CBD5E1] rounded-lg text-[#0F172A] font-sans text-xs p-[8px_12px] outline-none transition-colors duration-200 h-9 focus:border-[#0ea5a0] focus:bg-white" 
//                 value={filters.from_date} 
//                 onChange={e => setFilter("from_date", e.target.value)} 
//               />
//             </div>

//             <div className="flex flex-col gap-1.5 min-w-[150px]">
//               <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px]">To Date</label>
//               <input 
//                 type="date" 
//                 className="bg-[#F8FAFC] border border-[#CBD5E1] rounded-lg text-[#0F172A] font-sans text-xs p-[8px_12px] outline-none transition-colors duration-200 h-9 focus:border-[#0ea5a0] focus:bg-white" 
//                 value={filters.to_date} 
//                 onChange={e => setFilter("to_date", e.target.value)} 
//               />
//             </div>

//             <div className="flex flex-col gap-1.5 min-w-[150px]">
//               <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px]">Per Page</label>
//               <select 
//                 className="bg-[#F8FAFC] border border-[#CBD5E1] rounded-lg text-[#0F172A] font-sans text-xs p-[8px_12px] outline-none transition-colors duration-200 h-9 focus:border-[#0ea5a0] focus:bg-white" 
//                 value={filters.per_page} 
//                 onChange={e => setFilter("per_page", parseInt(e.target.value))}
//               >
//                 {[10, 20, 50, 100].map(n => <option key={n} value={n} className="bg-white">{n}</option>)}
//               </select>
//             </div>

//             <div className="flex gap-2 items-end">
//               <button 
//                 className="h-9 px-4 border border-transparent rounded-lg bg-gradient-to-br from-[#0A4ABF] to-[#0ea5a0] text-white font-sans text-xs font-semibold cursor-pointer transition-all duration-200 shadow-sm flex items-center gap-1.5 hover:opacity-90 hover:-translate-y-0.5" 
//                 onClick={applyFilters}
//               >
//                 Apply
//               </button>
//               <button 
//                 className="h-9 px-4 border rounded-lg bg-white text-[#64748B] border-[#CBD5E1] hover:border-[#0ea5a0] hover:text-[#0F172A] font-sans text-xs font-semibold cursor-pointer transition-all duration-200 shadow-sm flex items-center gap-1.5" 
//                 onClick={resetFilters}
//               >
//                 Reset
//               </button>
//             </div>
//           </div>
//         </div>

//         {/* Tabular Relational Database Display Workspace */}
//         <div className="bg-white border border-[#E2E8F0] shadow-sm rounded-[12px] overflow-hidden animate-[fadeUp_0.6s_ease_both]">
//           <div className="p-[14px_20px] border-b border-[#F1F5F9] flex items-center justify-between flex-wrap gap-2">
//             <div className="text-xs font-bold text-[#0F172A]">Device Records</div>
//             <div className="flex gap-3 items-center">
//               {loading && <div className="w-4 h-4 border-2 border-[rgba(14,165,160,0.3)] border-t-[#0ea5a0] rounded-full animate-spin flex-shrink-0" />}
//             </div>
//           </div>

//           <div className="overflow-x-auto">
//             <table className="w-full border-collapse text-xs">
//               <thead>
//                 <tr className="bg-[#F8FAFC]">
//                   <th className="p-[10px_14px] text-left text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px] border-b border-[#E2E8F0] whitespace-nowrap">Customer</th>
//                   <th className="p-[10px_14px] text-left text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px] border-b border-[#E2E8F0] whitespace-nowrap">Mobile</th>
//                   <th className="p-[10px_14px] text-left text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px] border-b border-[#E2E8F0] whitespace-nowrap">IMEI 1</th>
//                   <th className="p-[10px_14px] text-left text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px] border-b border-[#E2E8F0] whitespace-nowrap">Model</th>
//                   <th className="p-[10px_14px] text-left text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px] border-b border-[#E2E8F0] whitespace-nowrap">AI Model</th>
//                   <th className="p-[10px_14px] text-left text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px] border-b border-[#E2E8F0] whitespace-nowrap">Variant</th>
//                   <th className="p-[10px_14px] text-left text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px] border-b border-[#E2E8F0] whitespace-nowrap">Price</th>
//                   <th className="p-[10px_14px] text-left text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px] border-b border-[#E2E8F0] whitespace-nowrap">Match</th>
//                   <th className="p-[10px_14px] text-left text-[11px] font-bold text-[#64748B] uppercase tracking-[0.5px] border-b border-[#E2E8F0] whitespace-nowrap">Synced</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {loading && records.length === 0 ? (
//                   <SkeletonRows n={applied.per_page > 20 ? 10 : applied.per_page} />
//                 ) : records.length === 0 ? (
//                   <tr>
//                     <td colSpan={9} className="p-3.5 border-b-0">
//                       <div className="text-center p-[60px_20px] text-[#94A3B8]">
//                         <div className="text-4xl mb-3">🔍</div>
//                         <div className="text-[15px] font-bold text-[#64748B] mb-1.5">No records found</div>
//                         <div className="text-xs font-medium">Try adjusting your search or filters</div>
//                       </div>
//                     </td>
//                   </tr>
//                 ) : (
//                   records.map(r => (
//                     <tr key={r.id} className="group hover:bg-[#F8FAFC] transition-colors duration-100">
//                       <td className="p-[12px_14px] border-b border-[#F1F5F9] group-last:border-b-0 vertical-middle text-[#475569]">
//                         <div className="text-[#0F172A] font-semibold">{r.customer_name || "—"}</div>
//                         <div className="text-[11px] text-[#64748B] mt-0.5 font-medium">{r.email || ""}</div>
//                       </td>
//                       <td className="p-[12px_14px] border-b border-[#F1F5F9] group-last:border-b-0 vertical-middle text-[#334155] font-mono text-xs">
//                         {r.mobile || "—"}
//                       </td>
//                       <td className="p-[12px_14px] border-b border-[#F1F5F9] group-last:border-b-0 vertical-middle font-mono text-xs text-[#64748B]">
//                         {r.imei1 || "—"}
//                       </td>
//                       <td className="p-[12px_14px] border-b border-[#F1F5F9] group-last:border-b-0 vertical-middle text-[#475569]">
//                         <div className="text-xs font-semibold text-[#334155]">{r.brand || "—"}</div>
//                         <div className="text-xs text-[#64748B] font-mono">{r.model || ""}</div>
//                       </td>
//                       <td className="p-[12px_14px] border-b border-[#F1F5F9] group-last:border-b-0 vertical-middle text-[#097975] font-bold">
//                         {r.full_model_name || <span className="text-[#94A3B8]">—</span>}
//                       </td>
//                       <td className="p-[12px_14px] border-b border-[#F1F5F9] group-last:border-b-0 vertical-middle text-xs text-[#475569] font-medium">
//                         {r.variant || "—"}
//                       </td>
//                       <td className="p-[12px_14px] border-b border-[#F1F5F9] group-last:border-b-0 vertical-middle font-mono text-xs text-[#0ea5a0] font-bold">
//                         {formatPrice(r.approx_price || r.device_price)}
//                       </td>
//                       <td className="p-[12px_14px] border-b border-[#F1F5F9] group-last:border-b-0 vertical-middle text-[#475569]">
//                         {r.is_matched ? (
//                           <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[20px] text-[11px] font-bold bg-[#E6F4EA] border border-[#A3E635]/20 text-[#137333]">
//                             ✓ Matched
//                           </span>
//                         ) : (
//                           <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[20px] text-[11px] font-bold bg-[#FEF3C7] border border-[#F59E0B]/20 text-[#B45309]">
//                             Pending
//                           </span>
//                         )}
//                         {r.ai_processed && (
//                           <div className="mt-1">
//                             <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[20px] text-[11px] font-bold bg-[#E0F2FE] border border-[#0EA5E9]/20 text-[#0369A1]">
//                               AI ✦
//                             </span>
//                           </div>
//                         )}
//                       </td>
//                       <td className="p-[12px_14px] border-b border-[#F1F5F9] group-last:border-b-0 vertical-middle text-[11px] text-[#64748B] font-medium whitespace-nowrap">
//                         {formatDate(r.created_at)}
//                       </td>
//                     </tr>
//                   ))
//                 )}
//               </tbody>
//             </table>
//           </div>

//           {/* Pagination Navigation Ecosystem footer section */}
//           {pagination.last_page > 1 && (
//             <div className="p-[16px_20px] border-t border-[#F1F5F9] flex items-center justify-between flex-wrap gap-2.5 bg-[#F8FAFC]">
//               <div className="text-xs text-[#64748B] font-medium">
//                 Page {pagination.current_page} of {pagination.last_page} · {pagination.total?.toLocaleString()} total
//               </div>
//               <Paginator 
//                 page={pagination.current_page} 
//                 lastPage={pagination.last_page} 
//                 onPage={p => { 
//                   setPage(p); 
//                   window.scrollTo({ top: 0, behavior: "smooth" }); 
//                 }} 
//               />
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// }

// import { useState, useEffect, useCallback, useRef } from "react";

// const API_BASE = "https://uatwarranty.goelectronix.co.in/api";

// const BRAND_COLORS = {
//   navy: "#031B4E",
//   blue: "#0A4ABF",
//   teal: "#0ea5a0",
//   mint: "#1ED6C3",
// };

// const styles = `
//   @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

//   *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

//   :root {
//     --navy: #031B4E;
//     --blue: #0A4ABF;
//     --teal: #0ea5a0;
//     --mint: #1ED6C3;
//     --bg: #060F24;
//     --surface: #0D1B35;
//     --surface2: #122040;
//     --surface3: #162649;
//     --border: rgba(14,165,160,0.15);
//     --border2: rgba(10,74,191,0.25);
//     --text: #E8F0FF;
//     --text2: #8B9EC8;
//     --text3: #5A6E9A;
//     --matched: #10B981;
//     --unmatched: #F59E0B;
//     --radius: 12px;
//     --radius-sm: 8px;
//   }

//   body { background: var(--bg); font-family: 'Inter', sans-serif; color: var(--text); }

//   .panel { min-height: 100vh; background: var(--bg); }

//   .topbar {
//     position: sticky; top: 0; z-index: 100;
//     background: rgba(6,15,36,0.92);
//     backdrop-filter: blur(20px);
//     border-bottom: 1px solid var(--border);
//     padding: 0 24px;
//     height: 60px;
//     display: flex; align-items: center; justify-content: space-between;
//   }

//   .topbar-brand {
//     display: flex; align-items: center; gap: 10px;
//   }

//   .brand-pill {
//     background: linear-gradient(135deg, var(--blue), var(--teal));
//     border-radius: 8px;
//     width: 32px; height: 32px;
//     display: flex; align-items: center; justify-content: center;
//     font-size: 14px; font-weight: 700; color: #fff;
//   }

//   .brand-name { font-size: 15px; font-weight: 600; color: var(--text); letter-spacing: -0.3px; }
//   .brand-sub { font-size: 11px; color: var(--text3); font-weight: 400; }

//   .topbar-right { display: flex; align-items: center; gap: 12px; }

//   .live-badge {
//     display: flex; align-items: center; gap: 6px;
//     background: rgba(16,185,129,0.12);
//     border: 1px solid rgba(16,185,129,0.3);
//     border-radius: 20px; padding: 4px 10px;
//     font-size: 11px; font-weight: 500; color: #10B981;
//   }

//   .live-dot {
//     width: 6px; height: 6px; border-radius: 50%;
//     background: #10B981;
//     animation: pulse-green 2s infinite;
//   }

//   @keyframes pulse-green {
//     0%, 100% { opacity: 1; transform: scale(1); }
//     50% { opacity: 0.5; transform: scale(1.3); }
//   }

//   .main { padding: 24px; max-width: 1600px; margin: 0 auto; }

//   .summary-grid {
//     display: grid;
//     grid-template-columns: repeat(5, 1fr);
//     gap: 14px;
//     margin-bottom: 24px;
//   }

//   @media (max-width: 1100px) { .summary-grid { grid-template-columns: repeat(3, 1fr); } }
//   @media (max-width: 700px) { .summary-grid { grid-template-columns: repeat(2, 1fr); } }

//   .stat-card {
//     background: var(--surface);
//     border: 1px solid var(--border2);
//     border-radius: var(--radius);
//     padding: 18px 20px;
//     position: relative; overflow: hidden;
//     transition: border-color 0.2s, transform 0.2s;
//     animation: fadeUp 0.4s ease both;
//   }

//   .stat-card:hover { border-color: var(--teal); transform: translateY(-2px); }

//   .stat-card::before {
//     content: '';
//     position: absolute; top: 0; left: 0; right: 0; height: 2px;
//     background: linear-gradient(90deg, var(--blue), var(--teal));
//     opacity: 0;
//     transition: opacity 0.2s;
//   }

//   .stat-card:hover::before { opacity: 1; }

//   .stat-icon {
//     width: 36px; height: 36px; border-radius: var(--radius-sm);
//     display: flex; align-items: center; justify-content: center;
//     font-size: 16px; margin-bottom: 12px;
//   }

//   .stat-value {
//     font-size: 26px; font-weight: 700;
//     font-family: 'JetBrains Mono', monospace;
//     letter-spacing: -1px; line-height: 1;
//     margin-bottom: 4px;
//   }

//   .stat-label { font-size: 12px; color: var(--text2); font-weight: 500; text-transform: uppercase; letter-spacing: 0.5px; }

//   .stat-sub { font-size: 11px; color: var(--text3); margin-top: 6px; }

//   .match-bar {
//     height: 3px; background: var(--surface3);
//     border-radius: 2px; margin-top: 10px; overflow: hidden;
//   }

//   .match-fill {
//     height: 100%; border-radius: 2px;
//     background: linear-gradient(90deg, var(--teal), var(--mint));
//     transition: width 1s ease;
//   }

//   .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 24px; }
//   @media (max-width: 900px) { .two-col { grid-template-columns: 1fr; } }

//   .card {
//     background: var(--surface);
//     border: 1px solid var(--border2);
//     border-radius: var(--radius);
//     overflow: hidden;
//     animation: fadeUp 0.5s ease both;
//   }

//   .card-header {
//     padding: 16px 20px;
//     border-bottom: 1px solid var(--border);
//     display: flex; align-items: center; justify-content: space-between;
//   }

//   .card-title { font-size: 13px; font-weight: 600; color: var(--text); display: flex; align-items: center; gap: 8px; }
//   .card-title-icon { font-size: 15px; }

//   .rank-list { padding: 8px 0; }

//   .rank-item {
//     display: flex; align-items: center; gap: 12px;
//     padding: 10px 20px;
//     transition: background 0.15s;
//   }

//   .rank-item:hover { background: var(--surface2); }

//   .rank-num {
//     width: 20px; font-size: 11px; font-weight: 600;
//     color: var(--text3); text-align: center; flex-shrink: 0;
//     font-family: 'JetBrains Mono', monospace;
//   }

//   .rank-bar-wrap { flex: 1; }

//   .rank-name { font-size: 13px; font-weight: 500; color: var(--text); margin-bottom: 5px; }

//   .rank-track { height: 4px; background: var(--surface3); border-radius: 2px; overflow: hidden; }

//   .rank-fill {
//     height: 100%; border-radius: 2px;
//     background: linear-gradient(90deg, var(--blue), var(--teal));
//     transition: width 0.8s ease;
//   }

//   .rank-count {
//     font-size: 12px; font-weight: 600;
//     font-family: 'JetBrains Mono', monospace;
//     color: var(--text2); flex-shrink: 0; min-width: 36px; text-align: right;
//   }

//   .filters-bar {
//     background: var(--surface);
//     border: 1px solid var(--border2);
//     border-radius: var(--radius);
//     padding: 16px 20px;
//     margin-bottom: 20px;
//     animation: fadeUp 0.55s ease both;
//   }

//   .filters-row { display: flex; flex-wrap: wrap; gap: 10px; align-items: flex-end; }

//   .filter-group { display: flex; flex-direction: column; gap: 5px; min-width: 150px; }

//   .filter-label { font-size: 11px; font-weight: 500; color: var(--text3); text-transform: uppercase; letter-spacing: 0.5px; }

//   .filter-input, .filter-select {
//     background: var(--surface2);
//     border: 1px solid var(--border2);
//     border-radius: var(--radius-sm);
//     color: var(--text);
//     font-family: 'Inter', sans-serif;
//     font-size: 13px;
//     padding: 8px 12px;
//     outline: none;
//     transition: border-color 0.2s;
//     height: 36px;
//   }

//   .filter-input::placeholder { color: var(--text3); }
//   .filter-input:focus, .filter-select:focus { border-color: var(--teal); }
//   .filter-select option { background: var(--surface2); }

//   .filter-search { min-width: 220px; }

//   .btn {
//     height: 36px; padding: 0 16px; border: none; border-radius: var(--radius-sm);
//     font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 500;
//     cursor: pointer; transition: all 0.2s; display: flex; align-items: center; gap: 6px;
//   }

//   .btn-primary {
//     background: linear-gradient(135deg, var(--blue), var(--teal));
//     color: #fff;
//   }

//   .btn-primary:hover { opacity: 0.88; transform: translateY(-1px); }

//   .btn-ghost {
//     background: var(--surface2);
//     color: var(--text2);
//     border: 1px solid var(--border2);
//   }

//   .btn-ghost:hover { border-color: var(--teal); color: var(--text); }

//   .table-card {
//     background: var(--surface);
//     border: 1px solid var(--border2);
//     border-radius: var(--radius);
//     overflow: hidden;
//     animation: fadeUp 0.6s ease both;
//   }

//   .table-header {
//     padding: 14px 20px;
//     border-bottom: 1px solid var(--border);
//     display: flex; align-items: center; justify-content: space-between;
//     flex-wrap: wrap; gap: 10px;
//   }

//   .table-title { font-size: 13px; font-weight: 600; color: var(--text); }
//   .table-count { font-size: 12px; color: var(--text3); font-family: 'JetBrains Mono', monospace; }

//   .table-scroll { overflow-x: auto; }

//   table { width: 100%; border-collapse: collapse; font-size: 13px; }

//   thead { background: var(--surface2); }

//   th {
//     padding: 10px 14px;
//     text-align: left; font-size: 11px; font-weight: 600;
//     color: var(--text3); text-transform: uppercase; letter-spacing: 0.5px;
//     border-bottom: 1px solid var(--border); white-space: nowrap;
//   }

//   td {
//     padding: 12px 14px;
//     border-bottom: 1px solid rgba(14,165,160,0.07);
//     vertical-align: middle;
//     color: var(--text2);
//   }

//   tr:last-child td { border-bottom: none; }

//   tr:hover td { background: rgba(14,165,160,0.04); }

//   .td-name { color: var(--text); font-weight: 500; }

//   .td-imei { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: var(--text3); }

//   .td-model { color: var(--teal); font-weight: 500; }

//   .badge {
//     display: inline-flex; align-items: center; gap: 4px;
//     padding: 3px 8px; border-radius: 20px;
//     font-size: 11px; font-weight: 600;
//   }

//   .badge-matched { background: rgba(16,185,129,0.12); color: #10B981; border: 1px solid rgba(16,185,129,0.25); }
//   .badge-unmatched { background: rgba(245,158,11,0.12); color: #F59E0B; border: 1px solid rgba(245,158,11,0.25); }
//   .badge-ai { background: rgba(14,165,160,0.12); color: var(--mint); border: 1px solid rgba(14,165,160,0.25); }

//   .price-tag {
//     font-family: 'JetBrains Mono', monospace;
//     font-size: 12px; color: var(--mint);
//   }

//   .pagination {
//     padding: 16px 20px;
//     border-top: 1px solid var(--border);
//     display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px;
//   }

//   .pagination-info { font-size: 12px; color: var(--text3); }

//   .pagination-controls { display: flex; align-items: center; gap: 6px; }

//   .page-btn {
//     width: 32px; height: 32px;
//     background: var(--surface2);
//     border: 1px solid var(--border2);
//     border-radius: var(--radius-sm);
//     color: var(--text2);
//     font-size: 12px; font-weight: 500;
//     cursor: pointer; display: flex; align-items: center; justify-content: center;
//     transition: all 0.15s;
//     font-family: 'Inter', sans-serif;
//   }

//   .page-btn:hover:not(:disabled) { border-color: var(--teal); color: var(--text); }
//   .page-btn:disabled { opacity: 0.35; cursor: not-allowed; }
//   .page-btn.active { background: linear-gradient(135deg, var(--blue), var(--teal)); border-color: transparent; color: #fff; }

//   .skeleton {
//     background: linear-gradient(90deg, var(--surface2) 25%, var(--surface3) 50%, var(--surface2) 75%);
//     background-size: 200% 100%;
//     animation: shimmer 1.5s infinite;
//     border-radius: 4px;
//   }

//   @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

//   .sk-row td { padding: 14px; }

//   .empty-state {
//     text-align: center; padding: 60px 20px;
//     color: var(--text3);
//   }

//   .empty-icon { font-size: 40px; margin-bottom: 12px; }
//   .empty-title { font-size: 15px; font-weight: 600; color: var(--text2); margin-bottom: 6px; }
//   .empty-sub { font-size: 13px; }

//   .error-banner {
//     background: rgba(239,68,68,0.1);
//     border: 1px solid rgba(239,68,68,0.25);
//     border-radius: var(--radius-sm);
//     padding: 12px 16px;
//     font-size: 13px; color: #F87171;
//     margin-bottom: 16px;
//     display: flex; align-items: center; gap: 8px;
//   }

//   .spinner {
//     width: 16px; height: 16px;
//     border: 2px solid rgba(14,165,160,0.3);
//     border-top-color: var(--teal);
//     border-radius: 50%;
//     animation: spin 0.7s linear infinite;
//     flex-shrink: 0;
//   }

//   @keyframes spin { to { transform: rotate(360deg); } }

//   @keyframes fadeUp {
//     from { opacity: 0; transform: translateY(16px); }
//     to { opacity: 1; transform: translateY(0); }
//   }

//   .chip-group { display: flex; flex-wrap: wrap; gap: 6px; }

//   .chip {
//     padding: 4px 10px; border-radius: 20px;
//     font-size: 11px; font-weight: 500;
//     background: var(--surface2);
//     border: 1px solid var(--border2);
//     color: var(--text2);
//     cursor: pointer; transition: all 0.15s;
//   }

//   .chip.active, .chip:hover { background: rgba(14,165,160,0.15); border-color: var(--teal); color: var(--text); }

//   .divider { height: 1px; background: var(--border); margin: 0 20px; }
// `;

// const BRANDS = ["Vivo", "Realme", "Samsung", "OPPO", "Motorola", "Redmi", "Infinix", "Sony", "POCO", "Xiaomi"];

// function formatPrice(val) {
//   const n = parseFloat(val);
//   if (!n || n === 0) return "—";
//   return "₹" + n.toLocaleString("en-IN");
// }

// function formatBytes(bytes) {
//   if (!bytes) return "—";
//   const b = parseInt(bytes);
//   const gb = b / 1073741824;
//   return gb >= 1 ? gb.toFixed(1) + " GB" : (b / 1048576).toFixed(0) + " MB";
// }

// function formatDate(iso) {
//   if (!iso) return "—";
//   return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
// }

// function StatCard({ icon, label, value, sub, gradient, matchPct, delay }) {
//   return (
//     <div className="stat-card" style={{ animationDelay: delay }}>
//       <div className="stat-icon" style={{ background: gradient }}>
//         <span>{icon}</span>
//       </div>
//       <div className="stat-value" style={{ background: gradient, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
//         {value}
//       </div>
//       <div className="stat-label">{label}</div>
//       {sub && <div className="stat-sub">{sub}</div>}
//       {matchPct !== undefined && (
//         <div className="match-bar">
//           <div className="match-fill" style={{ width: matchPct + "%" }} />
//         </div>
//       )}
//     </div>
//   );
// }

// function RankChart({ title, icon, items, maxVal }) {
//   return (
//     <div className="card">
//       <div className="card-header">
//         <div className="card-title"><span className="card-title-icon">{icon}</span>{title}</div>
//         <span style={{ fontSize: 11, color: "var(--text3)" }}>Top {items.length}</span>
//       </div>
//       <div className="rank-list">
//         {items.map((item, i) => (
//           <div className="rank-item" key={i}>
//             <div className="rank-num">{String(i + 1).padStart(2, "0")}</div>
//             <div className="rank-bar-wrap">
//               <div className="rank-name">{item.full_model_name || item.brand || "—"}</div>
//               <div className="rank-track">
//                 <div className="rank-fill" style={{ width: ((item.total / maxVal) * 100) + "%" }} />
//               </div>
//             </div>
//             <div className="rank-count">{item.total.toLocaleString()}</div>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// }

// function SkeletonRows({ n = 8 }) {
//   return Array.from({ length: n }).map((_, i) => (
//     <tr className="sk-row" key={i}>
//       {Array.from({ length: 8 }).map((_, j) => (
//         <td key={j}><div className="skeleton" style={{ height: 14, width: j === 0 ? 130 : j === 1 ? 80 : 100 }} /></td>
//       ))}
//     </tr>
//   ));
// }

// function Paginator({ page, lastPage, onPage }) {
//   const pages = [];
//   const delta = 2;
//   const left = Math.max(1, page - delta);
//   const right = Math.min(lastPage, page + delta);
//   if (left > 1) pages.push(1, "...");
//   for (let p = left; p <= right; p++) pages.push(p);
//   if (right < lastPage) pages.push("...", lastPage);

//   return (
//     <div className="pagination-controls">
//       <button className="page-btn" onClick={() => onPage(page - 1)} disabled={page === 1}>‹</button>
//       {pages.map((p, i) =>
//         p === "..." ? (
//           <span key={i} style={{ color: "var(--text3)", fontSize: 12, padding: "0 4px" }}>…</span>
//         ) : (
//           <button
//             key={i}
//             className={`page-btn ${p === page ? "active" : ""}`}
//             onClick={() => onPage(p)}
//             >
//             {p}
//             </button>
//         )
//       )}
//       <button className="page-btn" onClick={() => onPage(page + 1)} disabled={page === lastPage}>›</button>
//     </div>
//   );
// }

// export default function SyncedDevicesPanel() {
//   const [data, setData] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   const [filters, setFilters] = useState({
//     search: "", match_status: "", brand: "", from_date: "", to_date: "", per_page: 20,
//   });

//   const [applied, setApplied] = useState({ ...filters });
//   const [page, setPage] = useState(1);

//   const debounceRef = useRef(null);

//   const fetchData = useCallback(async (f, pg) => {
//     setLoading(true);
//     setError(null);
//     try {
//       const params = new URLSearchParams();
//       if (f.search) params.set("search", f.search);
//       if (f.match_status) params.set("match_status", f.match_status);
//       if (f.brand) params.set("brand", f.brand);
//       if (f.from_date) params.set("from_date", f.from_date);
//       if (f.to_date) params.set("to_date", f.to_date);
//       params.set("per_page", f.per_page);
//       params.set("page", pg);

//       const res = await fetch(`${API_BASE}/synced-devices?${params}`);
//       if (!res.ok) throw new Error( `${res.status}`);
//       const json = await res.json();
//       setData(json);
//     } catch (e) {
//       setError(e.message);
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   useEffect(() => {
//     fetchData(applied, page);
//   }, [applied, page, fetchData]);

//   const handleSearchChange = (val) => {
//     setFilters(f => ({ ...f, search: val }));
//     clearTimeout(debounceRef.current);
//     debounceRef.current = setTimeout(() => {
//       setApplied(f => ({ ...f, search: val }));
//       setPage(1);
//     }, 450);
//   };

//   const applyFilters = () => { setApplied({ ...filters }); setPage(1); };

//   const resetFilters = () => {
//     const blank = { search: "", match_status: "", brand: "", from_date: "", to_date: "", per_page: 20 };
//     setFilters(blank);
//     setApplied(blank);
//     setPage(1);
//   };

//   const setFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));

//   const summary = data?.summary || {};
//   const topModels = data?.top_models || [];
//   const topBrands = data?.top_brands || [];
//   const records = data?.data || [];
//   const pagination = data?.pagination || {};

//   const maxModel = topModels[0]?.total || 1;
//   const maxBrand = topBrands[0]?.total || 1;

//   return (
//     <>
//       <style>{styles}</style>
//       <div className="panel">
//         <nav className="topbar">
//           <div className="topbar-brand">
//             <div className="brand-pill">G</div>
//             <div>
//               <div className="brand-name">GoElectronix</div>
//               <div className="brand-sub">Synced Devices Intelligence</div>
//             </div>
//           </div>
//           <div className="topbar-right">
//             <div className="live-badge">
//               <div className="live-dot" />
//               Live Data
//             </div>
//             <button className="btn btn-ghost" onClick={() => fetchData(applied, page)} style={{ gap: 6 }}>
//               {loading ? <div className="spinner" /> : "↻"} Refresh
//             </button>
//           </div>
//         </nav>

//         <div className="main">
//           {error && (
//             <div className="error-banner">
//               <span>⚠️</span> {error} — check your network or API status.
//             </div>
//           )}

//           <div className="summary-grid">
//             <StatCard icon="📱" label="Total Devices" value={summary.total_records?.toLocaleString() ?? "—"}
//               gradient="linear-gradient(135deg, #0A4ABF, #0ea5a0)" delay="0ms" />
//             <StatCard icon="✅" label="Matched" value={summary.matched?.toLocaleString() ?? "—"}
//               sub={`${summary.match_percentage ?? 0}% match rate`}
//               gradient="linear-gradient(135deg, #059669, #10B981)"
//               matchPct={summary.match_percentage}
//               delay="60ms" />
//             <StatCard icon="⏳" label="Unmatched" value={summary.not_matched?.toLocaleString() ?? "—"}
//               gradient="linear-gradient(135deg, #B45309, #F59E0B)" delay="120ms" />
//             <StatCard icon="🤖" label="AI Processed" value={summary.ai_processed?.toLocaleString() ?? "—"}
//               gradient="linear-gradient(135deg, #0ea5a0, #1ED6C3)"
//               sub={summary.total_records ? Math.round((summary.ai_processed / summary.total_records) * 100) + "% enriched" : ""}
//               delay="180ms" />
//             <StatCard icon="📊" label="Match Rate" value={(summary.match_percentage ?? 0) + "%"}
//               gradient="linear-gradient(135deg, #7C3AED, #A78BFA)"
//               matchPct={summary.match_percentage}
//               delay="240ms" />
//           </div>

//           {topModels.length > 0 && (
//             <div className="two-col">
//               <RankChart title="Top Models" icon="📱" items={topModels} maxVal={maxModel} />
//               <RankChart title="Top Brands" icon="🏷️" items={topBrands.filter(b => b.brand)} maxVal={maxBrand} />
//             </div>
//           )}

//           <div className="filters-bar">
//             <div className="filters-row">
//               <div className="filter-group" style={{ flex: "1 1 220px" }}>
//                 <label className="filter-label">Search</label>
//                 <input className="filter-input filter-search" placeholder="Name, IMEI, model, mobile…"
//                   value={filters.search} onChange={e => handleSearchChange(e.target.value)} />
//               </div>

//               <div className="filter-group">
//                 <label className="filter-label">Match Status</label>
//                 <select className="filter-select" value={filters.match_status} onChange={e => setFilter("match_status", e.target.value)}>
//                   <option value="">All</option>
//                   <option value="matched">Matched</option>
//                   <option value="not_matched">Not Matched</option>
//                 </select>
//               </div>

//               <div className="filter-group">
//                 <label className="filter-label">Brand</label>
//                 <select className="filter-select" value={filters.brand} onChange={e => setFilter("brand", e.target.value)}>
//                   <option value="">All Brands</option>
//                   {BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
//                 </select>
//               </div>

//               <div className="filter-group">
//                 <label className="filter-label">From Date</label>
//                 <input type="date" className="filter-input" value={filters.from_date} onChange={e => setFilter("from_date", e.target.value)} />
//               </div>

//               <div className="filter-group">
//                 <label className="filter-label">To Date</label>
//                 <input type="date" className="filter-input" value={filters.to_date} onChange={e => setFilter("to_date", e.target.value)} />
//               </div>

//               <div className="filter-group">
//                 <label className="filter-label">Per Page</label>
//                 <select className="filter-select" value={filters.per_page} onChange={e => setFilter("per_page", parseInt(e.target.value))}>
//                   {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
//                 </select>
//               </div>

//               <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
//                 <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
//                 <button className="btn btn-ghost" onClick={resetFilters}>Reset</button>
//               </div>
//             </div>
//           </div>

//           <div className="table-card">
//             <div className="table-header">
//               <div className="table-title">Device Records</div>
//               <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
//                 {loading && <div className="spinner" />}
//                 <div className="table-count">
//                   {/* {pagination.total ? `${pagination.total.toLocaleString()}records : ""} */}
//                 </div>
//               </div>
//             </div>

//             <div className="table-scroll">
//               <table>
//                 <thead>
//                   <tr>
//                     <th>Customer</th>
//                     <th>Mobile</th>
//                     <th>IMEI 1</th>
//                     <th>Model</th>
//                     <th>AI Model</th>
//                     <th>Variant</th>
//                     <th>Price</th>
//                     <th>Match</th>
//                     <th>Synced</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {loading && records.length === 0 ? (
//                     <SkeletonRows n={applied.per_page > 20 ? 10 : applied.per_page} />
//                   ) : records.length === 0 ? (
//                     <tr>
//                       <td colSpan={9}>
//                         <div className="empty-state">
//                           <div className="empty-icon">🔍</div>
//                           <div className="empty-title">No records found</div>
//                           <div className="empty-sub">Try adjusting your search or filters</div>
//                         </div>
//                       </td>
//                     </tr>
//                   ) : records.map(r => (
//                     <tr key={r.id}>
//                       <td>
//                         <div className="td-name">{r.customer_name || "—"}</div>
//                         <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>{r.email || ""}</div>
//                       </td>
//                       <td style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{r.mobile || "—"}</td>
//                       <td className="td-imei">{r.imei1 || "—"}</td>
//                       <td>
//                         <div style={{ fontSize: 12, color: "var(--text2)" }}>{r.brand || "—"}</div>
//                         <div style={{ fontSize: 12, color: "var(--text3)", fontFamily: "monospace" }}>{r.model || ""}</div>
//                       </td>
//                       <td className="td-model">{r.full_model_name || <span style={{ color: "var(--text3)" }}>—</span>}</td>
//                       <td style={{ fontSize: 12, color: "var(--text2)" }}>{r.variant || "—"}</td>
//                       <td className="price-tag">{formatPrice(r.approx_price || r.device_price)}</td>
//                       <td>
//                         {r.is_matched
//                           ? <span className="badge badge-matched">✓ Matched</span>
//                           : <span className="badge badge-unmatched">Pending</span>
//                         }
//                         {r.ai_processed && <div style={{ marginTop: 4 }}><span className="badge badge-ai">AI ✦</span></div>}
//                       </td>
//                       <td style={{ fontSize: 11, color: "var(--text3)", whiteSpace: "nowrap" }}>{formatDate(r.created_at)}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>

//             {pagination.last_page > 1 && (
//               <div className="pagination">
//                 <div className="pagination-info">
//                   Page {pagination.current_page} of {pagination.last_page} · {pagination.total?.toLocaleString()} total
//                 </div>
//                 <Paginator page={pagination.current_page} lastPage={pagination.last_page} onPage={p => { setPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </>
//   );
// }